import type { MaybeRefOrGetter } from 'vue';
import { onUnmounted, readonly, ref, toValue, watch } from 'vue';

interface DeferredRefOptions {
    /** Delay in ms before the ref turns `true`. Defaults to 600. */
    delayOn?: number
    /** Delay in ms before the ref turns `false`. Defaults to 0. */
    delayOff?: number
}

/**
 * Returns a readonly ref that mirrors `source` with configurable delays:
 * - Turning `true` is deferred by `delayOn` ms (prevents flickering on fast operations).
 * - Turning `false` is deferred by `delayOff` ms (0 by default, i.e. immediate).
 */
export function useDeferredRef(source: MaybeRefOrGetter<boolean>, options: DeferredRefOptions = {}) {
    const { delayOn = 0, delayOff = 0 } = options;
    const deferred = ref(false);
    let timer: ReturnType<typeof setTimeout> | null = null;

    watch(
        () => toValue(source),
        (value) => {
            if (timer !== null) {
                clearTimeout(timer);
                timer = null;
            }
            const delay = value ? delayOn : delayOff;
            if (delay > 0) {
                timer = setTimeout(() => {
                    deferred.value = value;
                    timer = null;
                }, delay);
            } else {
                deferred.value = value;
            }
        },
    );

    onUnmounted(() => {
        if (timer !== null) {
            clearTimeout(timer);
        }
    });

    return readonly(deferred);
}
